import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, useTheme } from '@mui/material';

import { StatusChip } from '@components/StatusChip';
import { CELL_STATE_COLORS, RACK_THEME } from '../../constants';

import type { RackCell } from '../../types';

type Props = {
  cell: RackCell;
};

const RackDragOverlay: React.FC<Props> = ({ cell }) => {
  const { t } = useTranslation('workOrderDetails');
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const colors = CELL_STATE_COLORS[cell.cellState];
  const isEmpty = cell.cellState === 'empty';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 0.5,
        p: 1.5,
        minHeight: 100,
        minWidth: 120,
        borderRadius: '4px',
        border: `2px solid ${isDarkMode ? colors.border.dark : colors.border.light}`,
        backgroundColor: isDarkMode
          ? colors.background.dark
          : colors.background.light,
        boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
        opacity: 0.9
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontFamily: 'monospace',
          color: isDarkMode
            ? RACK_THEME.labelColor.dark
            : RACK_THEME.labelColor.light,
          fontSize: '0.65rem',
          lineHeight: 1
        }}
      >
        [{cell.position.row},{cell.position.col}]
      </Typography>

      <Typography variant="body2" fontWeight={isEmpty ? 400 : 700}>
        {isEmpty
          ? t('rack.emptyCell')
          : cell.detail?.id || t('equipment.noAssetId')}
      </Typography>

      {!isEmpty && cell.detail?.status && (
        <StatusChip
          label={cell.detail.status}
          variant="status"
          size="small"
          sx={{ height: 20, fontSize: '0.65rem' }}
        />
      )}
    </Box>
  );
};

export default RackDragOverlay;
